/**
 * What a claim costs, computed from the rate tables rather than asserted.
 *
 * Two problems, and the second is the one that makes the first matter.
 *
 * The costs in this workbench were never computed. Every claim ported from the
 * mockup has `cost_per_fte_year` null and a dollar string sitting in
 * `cost_method` — the column meant to hold the *reasoning* — so the plain
 * surface's money line has never rendered for any of them. One claim's method
 * field holds a raw JSON blob.
 *
 * And the numbers that are there mix three different quantities without saying
 * which is which. "$11,385" against the claim to lift the conditionally
 * classified entry rate is the gap for **one teacher on that step**. "$4,100"
 * against the acting allowance claim looks like a **total annual cost to the
 * school**. A per-FTE figure is a third thing again. Putting them in the same
 * column under the same heading is how a group walks into a meeting and quotes
 * a number that is out by two orders of magnitude — and it is the employer who
 * will do that arithmetic first.
 *
 * So a costing here carries its basis, and the basis is part of the answer.
 *
 * What can be computed is bounded by what has been extracted, and as of the
 * allowance load that is all of Schedule A: Table 1A (salaries), 1B (positions
 * of responsibility), 1C (pre-school directors) and 3 (casual rates), plus
 * Award 17.1 and 19.2(b). `uncostable()` remains, because the bound will move
 * again — the NSW public award's tables are not structured, and neither is
 * Table 2 — and a claim that cannot be costed must say so rather than produce a
 * plausible number from the wrong table.
 */
export const BASIS_LABEL = {
    total_annual: 'a year, across the school',
    per_affected_teacher: 'a year, for each teacher it affects',
    per_fte_year: 'a year, for each full-time teacher',
    once: 'once',
};
/**
 * A costing nobody can check is a number the school gets to dispute for free.
 *
 * The rule this enforces: a figure computed from rate tables must name them.
 * `sources` empty and `author: 'ai'` together mean a model produced a number
 * from nothing, which is the one combination that should never reach a
 * representative.
 */
export function isGrounded(costing) {
    return costing.sources.length > 0;
}
/**
 * Lifting every teacher on one step to another step.
 *
 * The shape of the conditionally-classified-entry claim, and of any claim that
 * moves a band. Returns per-affected-teacher rather than a total, because how
 * many teachers sit on a given step is a staffing fact this corpus does not
 * hold — and inventing a headcount to produce a bigger number is exactly the
 * move that loses an argument.
 */
export function stepLift(from, to, table) {
    const before = table.rates.get(from);
    const after = table.rates.get(to);
    if (before === undefined || after === undefined)
        return null;
    return {
        amount: Math.round((after - before) * 100) / 100,
        basis: 'per_affected_teacher',
        method: `${to} (${currency(after)}) less ${from} (${currency(before)}), from ${table.tableCode}. ` +
            'Per teacher on that step — multiply by how many there are for a total, which this ' +
            'workbench does not know.',
        confidence: 'high',
        author: 'ai',
        sources: [table.tableCode],
    };
}
/**
 * Days of relief cover, which is what most conditions claims actually cost.
 *
 * Release time, training days, extra leave — all of them resolve to "somebody
 * has to stand in front of the class". Costed at a stated casual rate so the
 * school can argue the rate rather than the arithmetic.
 */
export function reliefDays(days, people, dayRate, fte, tableCode) {
    const total = days * people * dayRate;
    return {
        amount: Math.round((total / fte) * 100) / 100,
        basis: 'per_fte_year',
        method: `${people} × ${days} day${days === 1 ? '' : 's'} of relief at ${currency(dayRate)} ` +
            `(${tableCode}) is ${currency(total)}, across ${fte} teaching FTE.`,
        confidence: 'high',
        author: 'ai',
        sources: [tableCode],
    };
}
/**
 * Lifting an allowance, which is the shape of most claims about positions of
 * responsibility.
 *
 * Per holder rather than a total, for the same reason `stepLift` is: how many
 * teachers hold a Level 3 position is a staffing fact this corpus does not
 * hold, and inventing a headcount to produce a bigger number is the move that
 * loses the argument.
 */
export function allowanceLift(from, to, table) {
    const before = table.rates.get(from);
    const after = table.rates.get(to);
    if (before === undefined || after === undefined)
        return null;
    return {
        amount: Math.round((after - before) * 100) / 100,
        basis: 'per_affected_teacher',
        method: `${to} (${currency(after)}) less ${from} (${currency(before)}), from ${table.tableCode}. ` +
            'Per position holder — multiply by how many hold the position for a total.',
        confidence: 'high',
        author: 'ai',
        sources: [table.tableCode],
    };
}
/**
 * A claim that cannot be costed from what has been extracted.
 *
 * Returned rather than a zero or a guess. "$0" against a claim that plainly
 * costs something reads as "free", and half the seeded claims carry exactly
 * that — which is worse than an empty field, because a representative will
 * repeat it.
 */
export function uncostable(why) {
    return {
        amount: 0,
        basis: 'total_annual',
        method: `Not costed: ${why}`,
        confidence: 'low',
        author: 'ai',
        sources: [],
    };
}
export function currency(n) {
    return n.toLocaleString('en-AU', {
        style: 'currency',
        currency: 'AUD',
        maximumFractionDigits: 0,
    });
}
/** How a costing reads on a card, basis included because the basis is the point. */
export function describe(costing) {
    if (!isGrounded(costing))
        return costing.method;
    return `${currency(costing.amount)} ${BASIS_LABEL[costing.basis]}`;
}
//# sourceMappingURL=costing.js.map